import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useCart } from '../../context/CartContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const INITIAL_MESSAGE = {
  role: 'assistant',
  content: 'Bonjour ! Je suis votre guide gourmand des Halles. Que vous ferait plaisir aujourd\'hui ? \u{1F377}\u{1F9C0}',
};

function cleanMessage(text) {
  return text.replace(/\[ID:\d+\]/g, '');
}

function TypingIndicator() {
  return (
    <div className="flex items-start gap-2 mb-3">
      <div className="bg-white border border-[#F3DFC0] text-[#332A1C] rounded-2xl rounded-bl-sm px-4 py-3 max-w-[80%]">
        <div className="flex gap-1">
          <span className="w-2 h-2 bg-[#C4673D] rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-2 h-2 bg-[#C4673D] rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-2 h-2 bg-[#C4673D] rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

function ProductCard({ product, onAdd }) {
  return (
    <div className="flex items-center justify-between bg-white border border-[#F3DFC0] rounded-lg px-3 py-2 mt-1.5">
      <div className="min-w-0 mr-2">
        <p className="text-sm font-medium text-[#332A1C] truncate">{product.nom}</p>
        <p className="text-xs font-semibold text-[#C4673D]">{Number(product.prix_ttc).toFixed(2)} &euro;</p>
      </div>
      <button
        onClick={() => onAdd(product)}
        className="flex-shrink-0 text-xs font-medium text-white bg-[#C4673D] hover:bg-[#A8552F] px-2.5 py-1 rounded-md transition"
      >
        + Ajouter
      </button>
    </div>
  );
}

export default function ChatBot() {
  const location = useLocation();
  const { dispatch } = useCart();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_MESSAGE]);
  const [suggestedProducts, setSuggestedProducts] = useState({});
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Hide on backoffice routes
  if (location.pathname.startsWith('/backoffice')) {
    return null;
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleAddToCart = (product) => {
    dispatch({
      type: 'ADD',
      product: {
        id: product.id,
        nom: product.nom,
        prix_ttc: product.prix_ttc,
        stand_id: product.stand_id,
        stand_nom: product.stand_nom,
      },
    });
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: 'user', content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          conversation: newMessages.map(m => ({ role: m.role, content: m.content })),
        }),
      });

      if (!res.ok) throw new Error('Erreur serveur');

      const data = await res.json();
      const assistantMsg = { role: 'assistant', content: data.response || data.message || '' };
      const msgIndex = newMessages.length;

      setMessages(prev => [...prev, assistantMsg]);

      if (data.suggested_products && data.suggested_products.length > 0) {
        setSuggestedProducts(prev => ({ ...prev, [msgIndex]: data.suggested_products }));
      }
    } catch (err) {
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: 'Désolé, je rencontre un petit souci. Réessayez dans un instant !' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-[#C4673D] hover:bg-[#A8552F] text-white shadow-lg flex items-center justify-center transition z-50"
          aria-label="Ouvrir le chat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 w-80 sm:w-96 h-[500px] bg-[#FEFCF8] rounded-2xl shadow-2xl flex flex-col overflow-hidden z-50 border border-[#E2D1B8]">
          {/* Header */}
          <div className="bg-[#C4673D] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
            <h2 className="font-serif text-lg font-semibold">Votre sommelier virtuel</h2>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white transition"
              aria-label="Fermer le chat"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Messages */}
          <ScrollableMessages
            messages={messages}
            suggestedProducts={suggestedProducts}
            loading={loading}
            onAddToCart={handleAddToCart}
            messagesEndRef={messagesEndRef}
            scrollToBottom={scrollToBottom}
          />

          {/* Input */}
          <div className="bg-white border-t border-[#F3DFC0] px-3 py-2 flex gap-2 flex-shrink-0">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Votre question..."
              className="flex-1 text-sm text-[#332A1C] placeholder-[#8B7355] bg-[#FEFCF8] border border-[#E2D1B8] rounded-lg px-3 py-2 focus:outline-none focus:border-[#C4673D] transition"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !input.trim()}
              className="flex-shrink-0 w-9 h-9 rounded-lg bg-[#C4673D] hover:bg-[#A8552F] disabled:opacity-40 text-white flex items-center justify-center transition"
              aria-label="Envoyer"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function ScrollableMessages({ messages, suggestedProducts, loading, onAddToCart, messagesEndRef, scrollToBottom }) {
  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
      {messages.map((msg, i) => (
        <div key={i}>
          {msg.role === 'user' ? (
            <div className="flex justify-end mb-1">
              <div className="bg-[#C4673D] text-white rounded-2xl rounded-br-sm px-4 py-2 max-w-[80%] text-sm">
                {msg.content}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2 mb-1">
              <div className="bg-white border border-[#F3DFC0] text-[#332A1C] rounded-2xl rounded-bl-sm px-4 py-2 max-w-[80%] text-sm whitespace-pre-wrap">
                {cleanMessage(msg.content)}
              </div>
            </div>
          )}
          {suggestedProducts[i] && (
            <div className="ml-0 max-w-[85%] space-y-1 mb-2">
              {suggestedProducts[i].map((product) => (
                <ProductCard key={product.id} product={product} onAdd={onAddToCart} />
              ))}
            </div>
          )}
        </div>
      ))}
      {loading && <TypingIndicator />}
      <div ref={messagesEndRef} />
    </div>
  );
}
